/**
 * Plan-feature mapping for subscription gating.
 *
 * Plan hierarchy (lowest → highest):
 *   free → starter → professional → enterprise → unlimited (creator)
 *
 * If a user is on a trial that hasn't expired, they get Professional-level access.
 */

export const PLAN_LEVELS = {
  free: 0,
  starter: 1,
  professional: 2,
  enterprise: 3,
  unlimited: 99,
};

// Each gated feature requires at least this plan level
export const FEATURE_REQUIREMENTS = {
  // Free tier
  floor_plan: "free",
  reservations: "free",
  walk_ins: "free",
  basic_analytics: "free",
  servers: "free",
  sections: "free",
  // Starter
  members: "starter",
  sales: "starter",
  end_of_service: "starter",
  events: "starter",
  // Professional
  accounting: "professional",
  inventory: "professional",
  custom_sales_categories: "professional",
  member_tiers: "professional",
  peak_hours_heatmap: "professional",
  // Enterprise
  multi_location: "enterprise",
  custom_branding: "enterprise",
};

/**
 * Decide if `userPlan` (with optional trial flag) can access a feature.
 *
 * @param {string} userPlan - "free" | "starter" | "professional" | "enterprise" | "unlimited"
 * @param {string} feature - key from FEATURE_REQUIREMENTS
 * @param {object} options - { isTrialing: boolean, isCreator: boolean }
 * @returns {boolean}
 */
export function canAccess(userPlan, feature, { isTrialing = false, isCreator = false } = {}) {
  if (isCreator) return true;
  if (isTrialing) {
    // Trials get Professional-level access
    const req = PLAN_LEVELS[FEATURE_REQUIREMENTS[feature]] ?? 0;
    return req <= PLAN_LEVELS.professional;
  }
  const userLevel = PLAN_LEVELS[userPlan] ?? 0;
  const requiredLevel = PLAN_LEVELS[FEATURE_REQUIREMENTS[feature]] ?? 0;
  return userLevel >= requiredLevel;
}

export function getRequiredPlan(feature) {
  return FEATURE_REQUIREMENTS[feature] || "free";
}

export function planLabel(plan) {
  const labels = {
    free: "Free",
    starter: "Starter",
    professional: "Professional",
    enterprise: "Enterprise",
    unlimited: "Creator Unlimited",
  };
  return labels[plan] || "Free";
}
